import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setLoadedCompanyDocs, setSelectedCompanies } from '../../store/company/slice';
import { RootState } from '../../store/';
import { Text } from '../Typography';
import { getCompaniesWithDocuments } from '../../api/companyApi';
import CompanySection from './components/CompanySection';
import styles from './FileExplorer.module.scss';
import { Company, CompanyWithDocuments } from '../../store/company/types';
import TickerInput from '../TickerInput';
import IconClose from '../Icon/IconClose';
import { Button } from '../UI';
import { selectHasNonEmbeddedSelection } from '../../store/company/slice';
import { setFilters as setSearchFilters } from '../../store/search/slice';
import { trackEvent } from '../../services/tracking';

interface FileExplorerProps {
  onClose: () => void;
  isMobile: boolean;
  embeddedOnlyTicker?: boolean;
}

const FileExplorer: React.FC<FileExplorerProps> = ({ onClose, isMobile, embeddedOnlyTicker }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loadedCompanyDocs, selectedCompanies } = useAppSelector(
    (state: RootState) => state.company,
  );
  const hasNonEmbeddedSelection = useAppSelector(selectHasNonEmbeddedSelection);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCompanyDocuments = async () => {
      if (selectedCompanies.length === 0) {
        dispatch(setLoadedCompanyDocs([]));
        return;
      }

      setIsLoading(true);

      try {
        const companyIds = selectedCompanies.map((c) => c.id);
        const companiesWithDocs = await getCompaniesWithDocuments(companyIds);
        dispatch(setLoadedCompanyDocs(companiesWithDocs));
      } catch (error) {
        console.error('Error fetching company documents:', error);
        dispatch(setLoadedCompanyDocs([]));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyDocuments();
  }, [selectedCompanies, dispatch]);

  const handleTickerChange = (companies: Company[]) => {
    dispatch(setSelectedCompanies(companies));
    trackEvent('Company Selection Changed', {
      selectedCompanies: companies.map((c) => c.ticker),
      location: 'FileExplorer',
    });
  };

  const renderDocumentsContent = () => {
    if (isLoading) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.loadingSpinner} />
          <Text color="gray50">Loading documents...</Text>
        </div>
      );
    }

    if (selectedCompanies.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Text color="gray50">Select companies to view their documents</Text>
        </div>
      );
    }

    if (loadedCompanyDocs.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Text color="gray50">No companies found for the selected tickers</Text>
        </div>
      );
    }

    const hasDocuments = loadedCompanyDocs.some(
      (company) => company.documents && company.documents.length > 0,
    );

    if (!hasDocuments) {
      return (
        <div className={styles.emptyState}>
          <Text color="gray50">No documents found for the selected companies</Text>
        </div>
      );
    }

    return loadedCompanyDocs.map((company: CompanyWithDocuments) => (
      <React.Fragment key={company.id}>
        {loadedCompanyDocs.length > 1 && (
          <div className={styles.companyHeader}>
            <Text size="large" weight="semibold">
              {company.name}
            </Text>
          </div>
        )}
        <CompanySection company={company} />
      </React.Fragment>
    ));
  };

  return (
    <div className={styles.container}>
      {isMobile && (
        <Button
          variant="ghost"
          size="small"
          onClick={onClose}
          aria-label="Close"
          icon={<IconClose />}
          className={styles.closeButton}
        />
      )}
      <div className={styles.tickerSection}>
        <TickerInput
          value={selectedCompanies}
          onChange={handleTickerChange}
          isMulti
          embeddedOnly={embeddedOnlyTicker}
        />
        {!hasNonEmbeddedSelection && (
          <div className={styles.searchButton}>
            <Button
              variant="ghost"
              size="small"
              onClick={() => {
                trackEvent('FileExplorer Search Button Clicked', {
                  selectedCompanies: selectedCompanies.map((c) => c.ticker),
                });
                dispatch(setSearchFilters({ selectedCompanies }));
                navigate('/search');
              }}
            >
              Search company documents
            </Button>
          </div>
        )}
      </div>
      <div className={styles.documentsTree}>{renderDocumentsContent()}</div>
    </div>
  );
};

export default FileExplorer;
